import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const SUPPORT_EMAIL = "wclifhkfjlokfshntrkhm@gmail.com";

export const Route = createFileRoute("/_authenticated/support")({
  head: () => ({ meta: [{ title: "Support — CryptoBull Pro" }] }),
  component: Support,
});

function Support() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: tickets = [] } = useQuery({
    queryKey: ["tickets"],
    queryFn: async () => {
      const { data } = await supabase.from("tickets").select("*").order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: messages = [] } = useQuery({
    enabled: !!selected,
    queryKey: ["ticket-messages", selected],
    queryFn: async () => {
      const { data } = await supabase.from("ticket_messages").select("*").eq("ticket_id", selected!).order("created_at");
      return data ?? [];
    },
  });

  const createTicket = useMutation({
    mutationFn: async (input: { subject: string; body: string }) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("not signed in");
      const { data, error } = await supabase.from("tickets").insert({ user_id: u.user.id, subject: input.subject }).select().single();
      if (error) throw error;
      await supabase.from("ticket_messages").insert({ ticket_id: data.id, sender_id: u.user.id, body: input.body, is_admin: false });
      return data;
    },
    onSuccess: () => { toast.success("Ticket opened"); qc.invalidateQueries({ queryKey: ["tickets"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const reply = useMutation({
    mutationFn: async (body: string) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user || !selected) throw new Error("missing");
      await supabase.from("ticket_messages").insert({ ticket_id: selected, sender_id: u.user.id, body, is_admin: false });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket-messages", selected] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Support center</h1>
        <p className="text-sm text-muted-foreground">Open a ticket and our team will respond shortly.</p>
      </div>

      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="glass rounded-2xl p-4 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
      >
        <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-medium">Email support directly</div>
          <div className="text-xs text-muted-foreground break-all">{SUPPORT_EMAIL}</div>
        </div>
      </a>

      <div className="grid lg:grid-cols-2 gap-4">
        <form className="glass rounded-2xl p-6 space-y-4" onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          createTicket.mutate({ subject: String(fd.get("subject")), body: String(fd.get("body")) });
          (e.currentTarget as HTMLFormElement).reset();
        }}>
          <div className="text-sm font-medium">New ticket</div>
          <div className="space-y-2"><Label>Subject</Label><Input name="subject" required /></div>
          <div className="space-y-2"><Label>Message</Label><Textarea name="body" rows={5} required /></div>
          <Button type="submit" disabled={createTicket.isPending}>Submit ticket</Button>
        </form>

        <div className="glass rounded-2xl p-6">
          <div className="text-sm font-medium mb-3">Your tickets</div>
          {tickets.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No tickets</div> : (
            <div className="space-y-2">
              {tickets.map((t) => (
                <button key={t.id} onClick={() => setSelected(t.id)} className={`w-full text-left rounded-lg px-3 py-2 hover:bg-white/5 ${selected === t.id ? "bg-white/5" : ""}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">{t.subject}</span>
                    <Badge variant="outline" className="text-xs capitalize">{t.status}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(t.created_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="glass rounded-2xl p-6 space-y-3">
          <div className="text-sm font-medium">Conversation</div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {messages.map((m) => (
              <div key={m.id} className={`rounded-lg p-3 text-sm ${m.is_admin ? "bg-primary/10" : "bg-white/5"}`}>
                <div className="text-[11px] text-muted-foreground mb-1">{m.is_admin ? "Support" : "You"} · {fmtDate(m.created_at)}</div>
                <div>{m.body}</div>
              </div>
            ))}
          </div>
          <form className="flex gap-2" onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const body = String(fd.get("body")).trim();
            if (!body) return;
            reply.mutate(body);
            (e.currentTarget as HTMLFormElement).reset();
          }}>
            <Input name="body" placeholder="Write a reply…" />
            <Button type="submit">Send</Button>
          </form>
        </div>
      )}
    </div>
  );
}